/**
 * Leitor e Mapeador Inteligente de Planilhas CSV/Delimitadas
 * Processamento 100% Client-Side no navegador.
 * Identificação automática de separador, suporte a formatação BRL e correspondência "De-Para".
 */

export type TargetFieldKey = 
  | 'name'
  | 'costPrice'
  | 'salePrice'
  | 'unitCost'
  | 'unit'
  | 'stockQuantity'
  | 'category'
  | 'sku'
  | 'brand'
  | 'batchCode'
  | 'expirationDate'
  | 'ncm'
  | 'nbs'
  | 'fiscalCode'
  | 'itemType'
  | 'taxReduction'
  | 'cfop'
  | 'paymentTermDays'
  | 'purchaseTermDays'
  | 'sector'
  | 'legacyIcms'
  | 'legacyPis'
  | 'legacyCofins'
  | 'legacyIss';

export interface FieldDefinition {
  key: TargetFieldKey;
  label: string;
  required: boolean;
  patterns: string[];
}

export const TARGET_FIELDS: FieldDefinition[] = [
  {
    key: 'name',
    label: 'Descrição do Material / Insumo',
    required: true,
    patterns: [
      'des_item', 'descricao_item', 'nome', 'descricao', 'descriçao', 'descrição', 'produto', 'item', 
      'material', 'xprod', 'mercadoria', 'especificacao', 'especificação', 'titulo', 'título',
      'b1_desc', 'despro', 'itemdescription'
    ]
  },
  {
    key: 'costPrice',
    label: 'NF Entrada (Custo Total R$)',
    required: false,
    patterns: [
      'vl_total', 'valor_total', 'vtotal', 'linetotal', 'd1_total', 'vlrliq',
      'custo', 'preco_custo', 'preço_custo', 'precocusto', 'preçocusto', 
      'vlr_custo', 'vl_custo', 'vlrcusto', 'vl_compra', 'valor_compra', 
      'unitario', 'vuncom', 'preco_de_compra', 'preço_de_compra'
    ]
  },
  {
    key: 'unitCost',
    label: 'Custo Unitário de Aquisição (R$)',
    required: false,
    patterns: [
      'valor_aquisicao', 'd1_vunit', 'preuni', 'custo_unitario', 'preco_unitario', 
      'valor_unitario_compra', 'vlr_aquisicao', 'vlaquisicao'
    ]
  },
  {
    key: 'salePrice',
    label: 'Preço de Venda Praticado (Saída R$)',
    required: false,
    patterns: [
      'venda', 'preco_venda', 'preço_venda', 'precovenda', 'preçovenda', 
      'vlr_venda', 'vl_venda', 'vlrvenda', 'preco', 'preço', 'valor_unitario', 'pvenda',
      'b1_prv1', 'price', 'preco_venda_total'
    ]
  },
  {
    key: 'unit',
    label: 'Unidade de Medida (un, cx, kg)',
    required: false,
    patterns: ['unidade', 'unid', 'un', 'ucom', 'medida', 'um', 'unidade_medida']
  },
  {
    key: 'stockQuantity',
    label: 'Quantidade em Estoque',
    required: false,
    patterns: ['estoque', 'quantidade', 'qtd', 'qtde', 'saldo', 'saldo_atual', 'qcom', 'quant', 'estoque_atual']
  },
  {
    key: 'sku',
    label: 'Código / SKU',
    required: false,
    patterns: ['codigo', 'código', 'sku', 'ref', 'referencia', 'referência', 'cprod', 'cod', 'b1_cod', 'codpro', 'itemcode', 'cod_item']
  },
  {
    key: 'category',
    label: 'Categoria / Grupo',
    required: false,
    patterns: ['categoria', 'grupo', 'tipo', 'secao', 'seção', 'familia', 'família', 'classificacao']
  },
  {
    key: 'brand',
    label: 'Marca / Fabricante / Fornecedor',
    required: false,
    patterns: ['marca', 'fabricante', 'fornecedor', 'distribuidor', 'brand']
  },
  {
    key: 'batchCode',
    label: 'Lote de Controle',
    required: false,
    patterns: ['lote', 'nlote', 'numero_lote', 'número_lote', 'batch']
  },
  {
    key: 'expirationDate',
    label: 'Data de Validade',
    required: false,
    patterns: ['validade', 'vencimento', 'dval', 'data_validade', 'data_vencimento', 'expira']
  },
  {
    key: 'ncm',
    label: 'Código NCM (Mercadorias/Produtos Físicos)',
    required: false,
    patterns: ['ncm', 'ncm_extenso', 'cod_ncm', 'codigo_ncm', 'código_ncm', 'ncm_completo', 'posicao_ncm', 'sh', 'classificacao_fiscal_ncm', 'b1_posipi', 'codncm', 'ncmcode']
  },
  {
    key: 'nbs',
    label: 'Código NBS (Serviços/Intangíveis)',
    required: false,
    patterns: ['nbs', 'nbs_extenso', 'cod_nbs', 'codigo_nbs', 'código_nbs', 'servico_nbs', 'codigo_servico', 'código_serviço', 'nbs_completo', 'codnbs', 'nbscode']
  },
  {
    key: 'fiscalCode',
    label: 'Código Fiscal Unificado (NCM ou NBS)',
    required: false,
    patterns: ['ncm_nbs', 'codigo_fiscal', 'código_fiscal', 'classificacao_fiscal', 'classificação_fiscal', 'fiscal']
  },
  {
    key: 'itemType',
    label: 'Tipo de Item (Produto / Serviço)',
    required: false,
    patterns: ['tipo', 'tipo_item', 'tipo_produto', 'natureza', 'tp_item']
  },
  {
    key: 'taxReduction',
    label: 'Redução / Benefício Fiscal (Reforma)',
    required: false,
    patterns: ['reducao', 'redução', 'aliquota_reduzida', 'beneficio', 'benefício', 'enquadramento_reforma', 'isencao', 'isenção']
  },
  {
    key: 'cfop',
    label: 'CFOP (Operação Fiscal - Revenda / Uso & Consumo / Insumo)',
    required: false,
    patterns: ['cfop', 'cfop_extenso', 'cod_cfop', 'codigo_cfop', 'código_cfop', 'sufixo_cfop', 'cfop_descricao', 'cfop_padrao', 'nat_op', 'natureza_operacao', 'natureza_op', 'd1_cfop', 'codcfo', 'cfopcode']
  },
  {
    key: 'paymentTermDays',
    label: 'Prazo Médio de Venda / Recebimento (PMR em Dias)',
    required: false,
    patterns: ['prazo', 'prazo_dias', 'prazo_venda', 'pmr', 'dias_recebimento', 'vencimento_dias', 'condicao_recebimento']
  },
  {
    key: 'purchaseTermDays',
    label: 'Prazo Médio de Compra / Fornecedor (PMP em Dias)',
    required: false,
    patterns: ['prazo_compra', 'prazo_fornecedor', 'pmp', 'dias_pagamento', 'condicao_pagto', 'dias_fornecedor']
  },
  {
    key: 'sector',
    label: 'Segmento (Comércio / Serviço / Indústria)',
    required: false,
    patterns: ['setor', 'segmento', 'ramo', 'comercio_servico_industria', 'setor_operacional']
  },
  {
    key: 'legacyIcms',
    label: 'ICMS Destacado na NF de Entrada (Crédito Atual)',
    required: false,
    patterns: ['valor_icms', 'vl_icms', 'vlicms', 'd1_valicm', 'vlricm', 'icmsamount', 'vl_icms_recuperavel']
  },
  {
    key: 'legacyPis',
    label: 'PIS Destacado na NF de Entrada (Crédito Atual)',
    required: false,
    patterns: ['pis_recupera', 'pis_recuperado', 'valor_pis', 'vl_pis', 'd1_valpis', 'vlrpis', 'pisamount']
  },
  {
    key: 'legacyCofins',
    label: 'COFINS Destacado na NF de Entrada (Crédito Atual)',
    required: false,
    patterns: ['cofins_recuperado', 'cofins_recupera', 'valor_cofins', 'vl_cofins', 'd1_valcof', 'vlrcof', 'cofinsamount']
  },
  {
    key: 'legacyIss',
    label: 'ISS na Aquisição de Serviços',
    required: false,
    patterns: ['vl_iss', 'valor_iss', 'vl_issdevido', 'iss_retido']
  }
];

export interface ColumnMapping {
  [sourceHeader: string]: TargetFieldKey | 'ignore';
}

export interface ParsedCsvRawResult {
  delimiter: string;
  headers: string[];
  rows: Record<string, string>[];
  suggestedMappings: ColumnMapping;
  totalRows: number;
}

/**
 * Normaliza texto para comparação fonética/semântica simples
 */
function normalizeHeaderString(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

/**
 * Detecta o separador mais provável analisando as primeiras linhas
 */
export function detectDelimiter(text: string): string {
  const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0).slice(0, 5);
  if (lines.length === 0) return ';';

  const counts: Record<string, number> = {
    ';': 0,
    ',': 0,
    '\t': 0,
    '|': 0
  };

  for (const line of lines) {
    counts[';'] += (line.match(/;/g) || []).length;
    counts[','] += (line.match(/,/g) || []).length;
    counts['\t'] += (line.match(/\t/g) || []).length;
    counts['|'] += (line.match(/\|/g) || []).length;
  }

  let bestDelimiter = ';';
  let maxCount = -1;

  for (const [delim, count] of Object.entries(counts)) {
    if (count > maxCount) {
      maxCount = count;
      bestDelimiter = delim;
    }
  }

  return maxCount > 0 ? bestDelimiter : ';';
}

/**
 * Separa uma linha respeitando aspas duplas
 */
function parseDelimitedLine(line: string, delimiter: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++; // pula a aspa duplicada escapada
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === delimiter && !inQuotes) {
      fields.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  fields.push(current.trim());
  return fields;
}

/**
 * Converte valores monetários ou numéricos (pt-BR ou US) para number de forma estrita.
 * Suporta "R$ 1.250,50", "1.250,50", "1250,50", "1250.50", " 12,5 ".
 * Blindado contra NaN: se a conversão falhar ou o número for inválido, retorna o fallback fornecido.
 */
export function parseBrlNumber(val: string | number | null | undefined, fallback = 0): number {
  if (val === undefined || val === null || val === '') return fallback;
  if (typeof val === 'number') return (!isNaN(val) && isFinite(val)) ? val : fallback;

  let str = val.toString().trim().replace(/^R\$\s?/, '').replace(/\s+/g, '');
  if (!str) return fallback;

  // Se tiver ponto e vírgula, assume formato brasileiro: ex: 1.250.500,50
  if (str.includes('.') && str.includes(',')) {
    str = str.replace(/\./g, '').replace(',', '.');
  } else if (str.includes(',')) {
    // Apenas vírgula: "1250,50" -> "1250.50"
    str = str.replace(',', '.');
  } else if ((str.match(/\./g) || []).length > 1) {
    // Múltiplos pontos sem vírgula: ex: 1.250.500
    str = str.replace(/\./g, '');
  }

  const num = parseFloat(str);
  return (!isNaN(num) && isFinite(num)) ? num : fallback;
}

/**
 * Sugere mapeamento automático das colunas encontradas com base em pontuação de relevância.
 */
export function autoSuggestColumnMappings(headers: string[]): ColumnMapping {
  const mapping: ColumnMapping = {};
  const assignedTargets = new Set<TargetFieldKey>();

  for (const header of headers) {
    const normalized = normalizeHeaderString(header);
    const tokens = normalized.split('_');

    let bestTarget: TargetFieldKey | null = null;
    let highestScore = 0;

    for (const def of TARGET_FIELDS) {
      if (assignedTargets.has(def.key)) continue;

      for (const pattern of def.patterns) {
        let score = 0;

        if (normalized === pattern) {
          score = 100; // Correspondência exata
        } else if (tokens.includes(pattern)) {
          // Token isolado (ex: "codigo" em "codigo_item")
          // Se for uma palavra muito curta como "item" dentro de "codigo_item", dá prioridade para prefixos
          score = pattern.length >= 4 ? 70 : 50;
          if (tokens[0] === pattern) score += 10; // Primeiro token tem peso extra
        } else if (normalized.startsWith(pattern) || normalized.endsWith(pattern)) {
          score = 40 + pattern.length;
        } else if (normalized.includes(pattern) && pattern.length >= 4) {
          score = 20 + pattern.length;
        }

        if (score > highestScore) {
          highestScore = score;
          bestTarget = def.key;
        }
      }
    }

    if (bestTarget && highestScore >= 30) {
      mapping[header] = bestTarget;
      assignedTargets.add(bestTarget);
    } else {
      mapping[header] = 'ignore';
    }
  }

  return mapping;
}

export interface ErpPreset {
  id: 'auto' | '4shift' | 'totvs' | 'sap' | 'senior' | 'omie_bling';
  name: string;
  description: string;
  badge: string;
}

export const ERP_PRESETS: ErpPreset[] = [
  { id: 'auto', name: 'Detecção Inteligente', description: 'Varre e identifica automaticamente os cabeçalhos da planilha', badge: 'Auto' },
  { id: '4shift', name: '4Shift / Indústria & Requalificação', description: 'Layout 56 colunas com DES_ITEM, VL_TOTAL, CFOP, NCM e tributos', badge: '4Shift' },
  { id: 'totvs', name: 'Totvs Protheus / Datasul', description: 'Padrão B1_DESC, D1_TOTAL, B1_POSIPI, D1_CFOP, D1_VALICM', badge: 'Totvs' },
  { id: 'sap', name: 'SAP Business One / S4HANA', description: 'Padrão ItemDescription, LineTotal, NCMCode, CFOPCode', badge: 'SAP' },
  { id: 'senior', name: 'Senior Sapiens ERP', description: 'Padrão DesPro, VlrLiq, CodNcm, CodCfo, VlrIcm', badge: 'Senior' },
  { id: 'omie_bling', name: 'Omie / Bling / Tiny', description: 'Padrão PMEs com descricao, valor_unitario, ncm, cfop', badge: 'PMEs' },
];

/**
 * Aplica um preset pré-configurado de ERP às colunas encontradas
 */
export function applyErpPreset(headers: string[], presetId: ErpPreset['id']): ColumnMapping {
  if (presetId === 'auto') {
    return autoSuggestColumnMappings(headers);
  }

  const mapping: ColumnMapping = {};
  const assignedTargets = new Set<TargetFieldKey>();

  const presetRules: Record<Exclude<ErpPreset['id'], 'auto'>, Record<string, TargetFieldKey>> = {
    '4shift': {
      'des_item': 'name',
      'descricao_item': 'name',
      'sku': 'sku',
      'ncm_extenso': 'ncm',
      'nbs': 'nbs',
      'cfop_extenso': 'cfop',
      'cfop_descricao': 'cfop',
      'vl_total': 'costPrice',
      'valor_aquisicao': 'unitCost',
      'valor_icms': 'legacyIcms',
      'pis_recupera': 'legacyPis',
      'cofins_recuperado': 'legacyCofins',
      'vl_iss': 'legacyIss',
      'prazo': 'paymentTermDays',
      'pmp': 'purchaseTermDays',
    },
    'totvs': {
      'b1_desc': 'name',
      'b1_cod': 'sku',
      'b1_posipi': 'ncm',
      'd1_cfop': 'cfop',
      'd1_total': 'costPrice',
      'd1_vunit': 'unitCost',
      'b1_prv1': 'salePrice',
      'd1_valicm': 'legacyIcms',
      'd1_valpis': 'legacyPis',
      'd1_valcof': 'legacyCofins',
      'd1_valiss': 'legacyIss',
    },
    'sap': {
      'itemdescription': 'name',
      'itemname': 'name',
      'itemcode': 'sku',
      'ncmcode': 'ncm',
      'cfopcode': 'cfop',
      'linetotal': 'costPrice',
      'price': 'unitCost',
      'saleprice': 'salePrice',
      'icmsamount': 'legacyIcms',
      'pisamount': 'legacyPis',
      'cofinsamount': 'legacyCofins',
    },
    'senior': {
      'despro': 'name',
      'codpro': 'sku',
      'codncm': 'ncm',
      'codcfo': 'cfop',
      'vlrliq': 'costPrice',
      'preuni': 'unitCost',
      'preven': 'salePrice',
      'vlricm': 'legacyIcms',
      'vlrpis': 'legacyPis',
      'vlrcof': 'legacyCofins',
    },
    'omie_bling': {
      'descricao': 'name',
      'codigo': 'sku',
      'ncm': 'ncm',
      'cfop': 'cfop',
      'valor_total': 'costPrice',
      'valor_unitario': 'unitCost',
      'preco_venda': 'salePrice',
      'valor_icms': 'legacyIcms',
      'valor_pis': 'legacyPis',
      'valor_cofins': 'legacyCofins',
    }
  };

  const specificMap = presetRules[presetId];

  // 1ª passada: correspondência direta pelo dicionário do preset
  for (const header of headers) {
    const norm = normalizeHeaderString(header);
    if (specificMap && specificMap[norm] && !assignedTargets.has(specificMap[norm])) {
      mapping[header] = specificMap[norm];
      assignedTargets.add(specificMap[norm]);
    } else {
      mapping[header] = 'ignore';
    }
  }

  // 2ª passada: fallback de autodetecção para colunas ainda não preenchidas
  const fallback = autoSuggestColumnMappings(headers);
  for (const [hdr, target] of Object.entries(fallback)) {
    if (mapping[hdr] === 'ignore' && target !== 'ignore' && !assignedTargets.has(target)) {
      mapping[hdr] = target;
      assignedTargets.add(target);
    }
  }

  return mapping;
}

/**
 * Faz o parsing bruto da planilha CSV/delimitada
 */
export function parseRawSpreadsheet(content: string, customDelimiter?: string): ParsedCsvRawResult {
  const delimiter = customDelimiter || detectDelimiter(content);
  const rawLines = content.split(/\r?\n/).filter(line => line.trim().length > 0);

  if (rawLines.length === 0) {
    return {
      delimiter,
      headers: [],
      rows: [],
      suggestedMappings: {},
      totalRows: 0
    };
  }

  const headerLine = rawLines[0];
  const headers = parseDelimitedLine(headerLine, delimiter).map((h, i) => h || `Coluna_${i + 1}`);
  const suggestedMappings = autoSuggestColumnMappings(headers);

  const rows: Record<string, string>[] = [];

  for (let i = 1; i < rawLines.length; i++) {
    const fields = parseDelimitedLine(rawLines[i], delimiter);
    if (fields.length === 0 || (fields.length === 1 && !fields[0])) continue;

    const rowObj: Record<string, string> = {};
    headers.forEach((header, idx) => {
      rowObj[header] = fields[idx] || '';
    });
    rows.push(rowObj);
  }

  return {
    delimiter,
    headers,
    rows,
    suggestedMappings,
    totalRows: rows.length
  };
}

export interface ProcessedMigrationItem {
  id: string;
  name: string;
  costPrice: number;
  salePrice?: number;
  marginPercent?: number;
  minStock?: number;
  unit: string;
  stockQuantity: number;
  sku?: string;
  barcode?: string;
  taxableBarcode?: string;
  category?: string;
  brand?: string;
  batchCode?: string;
  expirationDate?: string;
  matchType?: 'sku' | 'barcode' | 'taxable_barcode' | 'name' | 'none';
  matchedExistingMaterialId?: string;
  matchedExistingMaterialName?: string;
  // Campos de Inteligência de Diluição de Custos
  stockControlMode?: 'unit_sale' | 'pot_open' | 'ingredient';
  yieldAmount?: number;
  yieldUnit?: string;
  unitCostDiluted?: number;
  dilutionRule?: string;
  isBuffetComponent?: boolean;
  itemType?: 'insumo' | 'produto_venda' | 'custo_fixo';
}

/**
 * Inteligência de Diluição de Custos e Sanitização de Preços Placeholder (R$ 0,01)
 * Preserva o custo real e dilui no rendimento ou no buffet da pista sem onerar o cliente.
 */
export function extractYieldAndDilution(
  name: string,
  costPrice: number,
  rawSalePrice: number,
  groupOrCategory?: string
): {
  stockControlMode: 'unit_sale' | 'pot_open' | 'ingredient';
  sanitizedSalePrice: number;
  marginPercent: number;
  yieldAmount: number;
  yieldUnit: string;
  unitCostDiluted: number;
  dilutionRule: string;
  isBuffetComponent: boolean;
  itemType: 'insumo' | 'produto_venda' | 'custo_fixo';
} {
  const upper = name.toUpperCase();
  const group = (groupOrCategory || '').toUpperCase();

  // 1. Detecção de Rendimento / Tamanho de Embalagem no Nome (ex: 25kg, 4kg, 10L, 500g, 3000 un)
  let yieldAmount = 1.0;
  let yieldUnit = 'un';

  const matchKg = upper.match(/(\d+(?:[,\.]\d+)?)\s*KG\b/);
  const matchG = upper.match(/(\d+(?:[,\.]\d+)?)\s*G(?:R)?\b/);
  const matchL = upper.match(/(\d+(?:[,\.]\d+)?)\s*L(?:ITROS?)?\b/);
  const matchMl = upper.match(/(\d+(?:[,\.]\d+)?)\s*ML\b/);
  const matchUn = upper.match(/(\d+)\s*(?:UN|UNIDADES|PCT|CX)\b/);

  if (matchKg) {
    yieldAmount = parseFloat(matchKg[1].replace(',', '.'));
    yieldUnit = 'kg';
  } else if (matchG) {
    yieldAmount = parseFloat(matchG[1].replace(',', '.')) / 1000.0;
    yieldUnit = 'kg';
  } else if (matchL) {
    yieldAmount = parseFloat(matchL[1].replace(',', '.'));
    yieldUnit = 'l';
  } else if (matchMl) {
    yieldAmount = parseFloat(matchMl[1].replace(',', '.')) / 1000.0;
    yieldUnit = 'l';
  } else if (matchUn) {
    yieldAmount = parseInt(matchUn[1], 10);
    yieldUnit = 'un';
  }

  const unitCostDiluted = yieldAmount > 0 ? (costPrice / yieldAmount) : costPrice;

  // 2. Detecção de preço placeholder legado (ex: R$ 0,01 do NoxMob para forçar pesagem)
  const isFakePlaceholderPrice = rawSalePrice <= 0.05;

  const isBuffet = (
    upper.includes('AÇAÍ') || upper.includes('ACAI') ||
    upper.includes('SORVETE') || upper.includes('SORV ') ||
    upper.includes('CREME DE ') || upper.includes('GELATO') ||
    group.includes('BAIXA MANUAL')
  ) && !['PICOLÉ', 'PICOLE', 'PALETA', 'COPO', 'POTINHO'].some(k => upper.includes(k));

  const isInsumoEmbalagem = [
    'LEITE', 'CHOCOLATE', 'COBERTURA', 'GUARDANAPO', 'COLHER', 'TAÇA', 'PASTILHA',
    'CASTANHA', 'GRANULADO', 'MARGARINA', 'CONFEITO', 'FARINHA', 'BASE', 'EMULSIFICANTE'
  ].some(k => upper.includes(k));

  const isRetailUnit = [
    'PICOLÉ', 'PICOLE', 'PALETA', 'ÁGUA', 'AGUA', 'REFRIGERANTE', 'COCA', 'SUCO', 'CERVEJA'
  ].some(k => upper.includes(k));

  if (isBuffet) {
    return {
      stockControlMode: 'pot_open',
      sanitizedSalePrice: 0, // R$ 0,00: diluído no quilo da pista (R$ 69,29/kg)
      marginPercent: 0,
      yieldAmount: yieldAmount > 1 ? yieldAmount : 1,
      yieldUnit: yieldUnit !== 'un' ? yieldUnit : 'pote',
      unitCostDiluted: Math.round(unitCostDiluted * 100) / 100,
      dilutionRule: 'Diluído no quilo da balança (Pista Buffet R$ 69,29/kg)',
      isBuffetComponent: true,
      itemType: 'insumo'
    };
  }

  if (isInsumoEmbalagem) {
    return {
      stockControlMode: 'ingredient',
      sanitizedSalePrice: 0,
      marginPercent: 0,
      yieldAmount,
      yieldUnit,
      unitCostDiluted: Math.round(unitCostDiluted * 10000) / 10000,
      dilutionRule: `Diluído no rendimento (${yieldAmount} ${yieldUnit}) = R$ ${unitCostDiluted.toFixed(2)}/${yieldUnit}`,
      isBuffetComponent: false,
      itemType: 'insumo'
    };
  }

  if (isRetailUnit) {
    // Produto de revenda unitária (picolé, refrigerante, etc.)
    const salePrice = !isFakePlaceholderPrice && rawSalePrice > 0 
      ? rawSalePrice 
      : (costPrice > 0 ? Math.round((costPrice / 0.60) * 100) / 100 : 0);
    const margin = costPrice > 0 && salePrice > 0 
      ? Math.round(((salePrice - costPrice) / salePrice) * 100) 
      : 40;

    return {
      stockControlMode: 'unit_sale',
      sanitizedSalePrice: salePrice,
      marginPercent: margin,
      yieldAmount: 1,
      yieldUnit: 'un',
      unitCostDiluted: costPrice,
      dilutionRule: 'Venda direta por unidade no caixa',
      isBuffetComponent: false,
      itemType: 'produto_venda'
    };
  }

  // Demais itens: se o preço for placeholder (ex: <= 0.05), trata como insumo diluído na operação
  if (isFakePlaceholderPrice) {
    return {
      stockControlMode: 'ingredient',
      sanitizedSalePrice: 0,
      marginPercent: 0,
      yieldAmount,
      yieldUnit,
      unitCostDiluted: Math.round(unitCostDiluted * 10000) / 10000,
      dilutionRule: `Diluído no custo operacional/receita (${yieldAmount} ${yieldUnit})`,
      isBuffetComponent: false,
      itemType: 'insumo'
    };
  }

  // Venda direta padrão com margem real
  const margin = costPrice > 0 && rawSalePrice > 0 
    ? Math.round(((rawSalePrice - costPrice) / rawSalePrice) * 100) 
    : 40;

  return {
    stockControlMode: 'unit_sale',
    sanitizedSalePrice: rawSalePrice,
    marginPercent: margin,
    yieldAmount: 1,
    yieldUnit: 'un',
    unitCostDiluted: costPrice,
    dilutionRule: 'Venda direta no caixa',
    isBuffetComponent: false,
    itemType: 'produto_venda'
  };
}

/**
 * Converte as linhas brutas nos itens padronizados do R-MAX usando o mapeamento configurado
 */
export function convertRowsWithMapping(
  rows: Record<string, string>[],
  mapping: ColumnMapping
): ProcessedMigrationItem[] {
  // Encontra qual cabeçalho mapeia para qual target
  const targetToHeader: Partial<Record<TargetFieldKey, string>> = {};
  for (const [header, target] of Object.entries(mapping)) {
    if (target !== 'ignore') {
      targetToHeader[target] = header;
    }
  }

  const nameHeader = targetToHeader['name'];
  if (!nameHeader) return [];

  const items: ProcessedMigrationItem[] = [];

  rows.forEach((row, idx) => {
    const rawName = row[nameHeader]?.trim();
    if (!rawName) return; // Ignora linhas sem nome

    const costPrice = targetToHeader['costPrice'] 
      ? parseBrlNumber(row[targetToHeader['costPrice']], 0) 
      : 0;

    const rawSalePrice = targetToHeader['salePrice'] 
      ? parseBrlNumber(row[targetToHeader['salePrice']], 0) 
      : 0;

    const rawUnit = targetToHeader['unit'] 
      ? (row[targetToHeader['unit']]?.trim().toLowerCase() || 'un') 
      : 'un';

    const category = targetToHeader['category'] ? row[targetToHeader['category']]?.trim() : undefined;

    // Aplica Inteligência de Diluição
    const dilution = extractYieldAndDilution(rawName, costPrice, rawSalePrice, category);

    const stockQuantity = targetToHeader['stockQuantity'] 
      ? parseBrlNumber(row[targetToHeader['stockQuantity']], 0) 
      : 0;

    const sku = targetToHeader['sku'] ? row[targetToHeader['sku']]?.trim() : undefined;
    const brand = targetToHeader['brand'] ? row[targetToHeader['brand']]?.trim() : undefined;
    const batchCode = targetToHeader['batchCode'] ? row[targetToHeader['batchCode']]?.trim() : undefined;
    const expirationDate = targetToHeader['expirationDate'] ? row[targetToHeader['expirationDate']]?.trim() : undefined;

    items.push({
      id: `mig_item_${Date.now()}_${idx}`,
      name: rawName,
      costPrice,
      salePrice: dilution.sanitizedSalePrice > 0 ? dilution.sanitizedSalePrice : undefined,
      marginPercent: dilution.marginPercent,
      minStock: 0,
      unit: rawUnit || dilution.yieldUnit || 'un',
      stockQuantity,
      sku: sku || undefined,
      category: category || (dilution.isBuffetComponent ? 'Buffet' : 'variável'),
      brand: brand || undefined,
      batchCode: batchCode || undefined,
      expirationDate: expirationDate || undefined,
      matchType: 'none',
      stockControlMode: dilution.stockControlMode,
      yieldAmount: dilution.yieldAmount,
      yieldUnit: dilution.yieldUnit,
      unitCostDiluted: dilution.unitCostDiluted,
      dilutionRule: dilution.dilutionRule,
      isBuffetComponent: dilution.isBuffetComponent,
      itemType: dilution.itemType
    });
  });

  return items;
}
